import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import {
  UploadSimple, Check, X, CaretRight, CaretLeft, FileXls, FileCsv,
  Spinner, Sparkle,
} from "@phosphor-icons/react";

/**
 * SpreadsheetImport (v3.6)
 *
 * Four-stage flow:
 *  1) Upload     — drag/drop .csv or .xlsx
 *  2) Map        — confirm or override which header maps to which field
 *  3) Plan       — Leo (GPT-5.2) proposes a class structure + per-row assignment
 *  4) Review     — toggle classes, override per-row class, then commit
 *
 * Posts to /api/assets/bulk-import/{parse,plan,commit}. All steps are
 * recoverable via the Back button — no data is persisted until Commit.
 */

const CANONICAL_FIELDS = [
  { key: "_skip", label: "— Skip —" },
  { key: "name", label: "Name (required)" },
  { key: "brand", label: "Brand" },
  { key: "model", label: "Model" },
  { key: "serial", label: "Serial" },
  { key: "fmv", label: "FMV ($)" },
  { key: "rcv", label: "RCV ($)" },
  { key: "purchase_price", label: "Purchase Price" },
  { key: "purchase_date", label: "Purchase Date" },
  { key: "location", label: "Location / Room" },
  { key: "condition", label: "Condition" },
  { key: "provenance", label: "Provenance" },
  { key: "notes", label: "Notes" },
];

export default function SpreadsheetImport({ onDone }) {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [parse, setParse] = useState(null); // {headers, rows, suggested_mapping, count}
  const [mapping, setMapping] = useState({}); // header -> canonical
  const [plan, setPlan] = useState(null); // {proposed_classes, row_assignments, existing_classes}
  const [acceptedClasses, setAcceptedClasses] = useState({}); // label -> true
  const [rowAssignments, setRowAssignments] = useState({}); // row_index -> class_label
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setStep(1); setFile(null); setParse(null); setMapping({});
    setPlan(null); setAcceptedClasses({}); setRowAssignments({});
  };

  // ----- Step 1: Upload -----
  const onPick = (f) => {
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith(".csv") && !name.endsWith(".xlsx")) {
      toast.error("Please choose a .csv or .xlsx file.");
      return;
    }
    setFile(f);
  };

  const onParse = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/assets/bulk-import/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setParse(r.data);
      setMapping(r.data.suggested_mapping || {});
      setStep(2);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Parse failed.");
    } finally {
      setBusy(false);
    }
  };

  // ----- Step 2: Map -----
  const onPlan = async () => {
    if (!parse) return;
    const hasName = Object.values(mapping).includes("name");
    if (!hasName) {
      toast.error("At least one column must be mapped to Name.");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(
        "/assets/bulk-import/plan",
        { rows: parse.rows, mapping },
        { timeout: 120000 },
      );
      setPlan(r.data);
      // Default: accept every proposed class
      const accepted = {};
      (r.data.proposed_classes || []).forEach((c) => { accepted[c.label] = true; });
      setAcceptedClasses(accepted);
      // Seed per-row assignments from Leo's plan
      const ra = {};
      (r.data.row_assignments || []).forEach((a) => { ra[a.row_index] = a.class_label; });
      setRowAssignments(ra);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Leo could not plan the import.");
    } finally {
      setBusy(false);
    }
  };

  // ----- Step 3: Review + Commit -----
  const allLabels = () => {
    const out = new Set(["Other"]);
    (plan?.existing_classes || []).forEach((c) => out.add(c.label));
    (plan?.proposed_classes || []).forEach((c) => {
      // Only offer proposed labels that the user has accepted
      if (acceptedClasses[c.label]) out.add(c.label);
    });
    return Array.from(out);
  };

  const onCommit = async () => {
    if (!parse) return;
    // Resolve assignments: if user un-accepted a class, rewrite those rows to "Other"
    const labels = new Set(allLabels());
    const finalAssignments = parse.rows.map((_, idx) => {
      const chosen = rowAssignments[idx] || "Other";
      return { row_index: idx, class_label: labels.has(chosen) ? chosen : "Other" };
    });
    const accepted = (plan?.proposed_classes || []).filter((c) => acceptedClasses[c.label]);
    setBusy(true);
    try {
      const r = await api.post(
        "/assets/bulk-import/commit",
        {
          rows: parse.rows,
          mapping,
          accepted_classes: accepted,
          accepted_assignments: finalAssignments,
        },
        { timeout: 120000 },
      );
      const { inserted, skipped, errors, created_classes } = r.data;
      toast.success(
        `Imported ${inserted} item(s)${created_classes.length ? ` · ${created_classes.length} new class(es)` : ""}${skipped ? ` · ${skipped} skipped` : ""}.`
      );
      if ((errors || []).length) {
        toast.error(`${errors.length} row(s) had issues — see preview.`);
      }
      reset();
      onDone && onDone();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Import failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="spreadsheet-import">
      <StepHeader step={step} />

      {step === 1 && (
        <UploadStep
          file={file} onPick={onPick} onParse={onParse} busy={busy}
        />
      )}

      {step === 2 && parse && (
        <MapStep
          headers={parse.headers} rows={parse.rows}
          mapping={mapping} setMapping={setMapping}
          onBack={() => setStep(1)} onNext={onPlan} busy={busy}
        />
      )}

      {step === 3 && plan && parse && (
        <ReviewStep
          plan={plan} parse={parse} mapping={mapping}
          acceptedClasses={acceptedClasses} setAcceptedClasses={setAcceptedClasses}
          rowAssignments={rowAssignments} setRowAssignments={setRowAssignments}
          allLabels={allLabels} onBack={() => setStep(2)} onCommit={onCommit}
          busy={busy}
        />
      )}
    </div>
  );
}

function StepHeader({ step }) {
  const steps = ["Upload", "Map columns", "Review & commit"];
  return (
    <div className="flex items-center gap-2 font-mono-ledger text-[10px] uppercase tracking-[0.25em]">
      {steps.map((s, i) => {
        const idx = i + 1;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={s} className="flex items-center gap-2">
            <span className={`w-5 h-5 inline-flex items-center justify-center border ${active ? "bg-[#1A3622] text-[#F9F8F6] border-[#0F2818]" : done ? "bg-[#C5A059] text-[#0A192F] border-[#A88040]" : "border-[#E0DDD6] text-[#5C5F58]"}`}>
              {idx}
            </span>
            <span className={active ? "text-[#1A3622]" : done ? "text-[#5C5F58]" : "text-[#A8A8A8]"}>{s}</span>
            {idx < steps.length && <CaretRight size={10} className="text-[#A8A8A8]" />}
          </div>
        );
      })}
    </div>
  );
}

function UploadStep({ file, onPick, onParse, busy }) {
  const [dragOver, setDragOver] = useState(false);
  return (
    <div className="space-y-4">
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onPick(e.dataTransfer.files?.[0]);
        }}
        className={`border-2 border-dashed p-10 text-center bg-white transition-colors ${dragOver ? "border-[#1A3622] bg-[#F9F8F6]" : "border-[#E0DDD6]"}`}
      >
        <UploadSimple size={36} className="mx-auto text-[#5C5F58] mb-3" />
        <div className="font-serif-display text-xl text-[#0A192F]">Drag your spreadsheet here</div>
        <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mt-2">
          .csv or .xlsx · up to 500 rows
        </div>
        <div className="mt-5">
          <label className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-4 py-2 text-sm border border-[#0F2818] hover:bg-[#0F2818] cursor-pointer" data-testid="upload-browse">
            <UploadSimple size={14} weight="bold" /> Browse files
            <input
              type="file" accept=".csv,.xlsx" className="hidden"
              onChange={(e) => onPick(e.target.files?.[0])}
              data-testid="upload-input"
            />
          </label>
        </div>
        {file && (
          <div className="mt-5 inline-flex items-center gap-2 border border-[#E0DDD6] bg-[#F9F8F6] px-3 py-1.5 text-xs" data-testid="upload-filename">
            {file.name.toLowerCase().endsWith(".xlsx") ? <FileXls size={14} /> : <FileCsv size={14} />}
            <span>{file.name}</span>
            <span className="text-[#5C5F58] font-mono-ledger text-[10px]">{(file.size / 1024).toFixed(1)} KB</span>
          </div>
        )}
      </div>
      <div className="flex justify-end">
        <button
          onClick={onParse} disabled={!file || busy}
          data-testid="upload-next"
          className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
        >
          {busy ? <Spinner size={14} className="animate-spin" /> : <CaretRight size={14} weight="bold" />}
          {busy ? "Parsing…" : "Continue"}
        </button>
      </div>
    </div>
  );
}

function MapStep({ headers, rows, mapping, setMapping, onBack, onNext, busy }) {
  return (
    <div className="space-y-4">
      <div className="border border-[#E0DDD6] bg-white">
        <div className="px-4 py-3 border-b border-[#E0DDD6] flex items-baseline justify-between">
          <div>
            <div className="font-serif-display text-lg text-[#0A192F]">Map your columns</div>
            <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mt-1">
              {rows.length} row(s) · {headers.length} column(s) · auto-detected mapping shown
            </div>
          </div>
        </div>
        <div className="divide-y divide-[#E0DDD6]" data-testid="mapping-grid">
          {headers.map((h, i) => {
            const sample = rows.slice(0, 3).map((r) => r[h]).filter(Boolean).slice(0, 3).join(" · ");
            return (
              <div key={`${h}-${i}`} className="grid grid-cols-12 gap-3 px-4 py-3 items-center">
                <div className="col-span-4">
                  <div className="font-serif-display text-base text-[#0A192F]">{h || "(unnamed)"}</div>
                  <div className="font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58] truncate" title={sample}>
                    {sample || "—"}
                  </div>
                </div>
                <div className="col-span-1 text-[#A8A8A8]">→</div>
                <div className="col-span-7">
                  <select
                    value={mapping[h] || "_skip"}
                    onChange={(e) => setMapping({ ...mapping, [h]: e.target.value })}
                    data-testid={`map-${h}`}
                    className="w-full bg-[#F9F8F6] border border-[#E0DDD6] px-3 py-1.5 text-sm focus:outline-none focus:border-[#1A3622]"
                  >
                    {CANONICAL_FIELDS.map((f) => (
                      <option key={f.key} value={f.key}>{f.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 border border-[#E0DDD6] text-[#5C5F58] px-4 py-2 text-sm hover:border-[#5C5F58]"
        >
          <CaretLeft size={14} weight="bold" /> Back
        </button>
        <button
          onClick={onNext} disabled={busy}
          data-testid="map-next"
          className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
        >
          {busy ? <Spinner size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
          {busy ? "Leo is reading…" : "Ask Leo to classify"}
        </button>
      </div>
    </div>
  );
}

function ReviewStep({
  plan, parse, mapping, acceptedClasses, setAcceptedClasses,
  rowAssignments, setRowAssignments, allLabels, onBack, onCommit, busy,
}) {
  const labels = allLabels();
  const projected = parse.rows.map((r, idx) => {
    const out = { _row_index: idx };
    Object.entries(mapping).forEach(([h, fld]) => {
      if (fld && fld !== "_skip") out[fld] = r[h];
    });
    return out;
  });

  return (
    <div className="space-y-6">
      {/* Leo's proposed classes */}
      <div className="border border-[#E0DDD6] border-l-4 border-l-[#C5A059] bg-white p-5">
        <div className="flex items-center gap-2 mb-2">
          <Sparkle size={16} weight="fill" color="#C5A059" />
          <span className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#1A3622] font-bold">
            Leo&apos;s Proposed Structure
          </span>
        </div>
        {plan.proposed_classes.length === 0 ? (
          <div className="text-sm text-[#5C5F58] italic font-serif-display">
            No new classes proposed — Leo placed everything into your existing taxonomy or under Other.
          </div>
        ) : (
          <div className="space-y-2" data-testid="proposed-classes">
            {plan.proposed_classes.map((c) => (
              <label key={c.label} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!acceptedClasses[c.label]}
                  onChange={(e) => setAcceptedClasses({ ...acceptedClasses, [c.label]: e.target.checked })}
                  data-testid={`accept-class-${c.label}`}
                  className="accent-[#1A3622] mt-1.5"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-serif-display text-base text-[#0A192F]">{c.label}</span>
                    <span className="font-mono-ledger text-[9px] uppercase tracking-[0.2em] text-[#5C5F58]">
                      · {c.confidence} confidence
                    </span>
                  </div>
                  <div className="text-xs text-[#5C5F58] italic font-serif-display mt-0.5">{c.rationale}</div>
                </div>
              </label>
            ))}
          </div>
        )}
      </div>

      {/* Per-row review table */}
      <div className="border border-[#E0DDD6] bg-white">
        <div className="px-4 py-3 border-b border-[#E0DDD6]">
          <div className="font-serif-display text-lg text-[#0A192F]">Review {projected.length} item(s)</div>
          <div className="font-mono-ledger text-[10px] uppercase tracking-[0.25em] text-[#5C5F58] mt-1">
            Override any row&apos;s class · uncheck a class above to fall back to Other
          </div>
        </div>
        <div className="max-h-[460px] overflow-auto" data-testid="review-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F9F8F6] sticky top-0">
              <tr className="text-left font-mono-ledger text-[10px] uppercase tracking-[0.2em] text-[#5C5F58]">
                <th className="px-3 py-2 w-10">#</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2 w-24">Brand</th>
                <th className="px-3 py-2 w-20 text-right">FMV</th>
                <th className="px-3 py-2 w-56">Class</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F0EDE6]">
              {projected.map((r) => (
                <tr key={r._row_index} data-testid={`review-row-${r._row_index}`}>
                  <td className="px-3 py-2 font-mono-ledger text-[10px] text-[#5C5F58]">{String(r._row_index + 1).padStart(3, "0")}</td>
                  <td className="px-3 py-2 text-[#0A192F] truncate max-w-[280px]" title={r.name}>
                    {r.name || <span className="text-[#C9C4B8] italic">(no name)</span>}
                  </td>
                  <td className="px-3 py-2 text-[#5C5F58] truncate">{r.brand || "—"}</td>
                  <td className="px-3 py-2 text-right tabular-nums text-[#0A192F]">
                    {r.fmv ? `$${Number(String(r.fmv).replace(/[^0-9.\-]/g, "")) ? Number(String(r.fmv).replace(/[^0-9.\-]/g, "")).toLocaleString() : r.fmv}` : "—"}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      value={rowAssignments[r._row_index] || "Other"}
                      onChange={(e) => setRowAssignments({ ...rowAssignments, [r._row_index]: e.target.value })}
                      data-testid={`row-class-${r._row_index}`}
                      className="w-full bg-[#F9F8F6] border border-[#E0DDD6] px-2 py-1 text-xs focus:outline-none focus:border-[#1A3622]"
                    >
                      {labels.map((lbl) => (
                        <option key={lbl} value={lbl}>{lbl}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="inline-flex items-center gap-2 border border-[#E0DDD6] text-[#5C5F58] px-4 py-2 text-sm hover:border-[#5C5F58]"
        >
          <CaretLeft size={14} weight="bold" /> Back
        </button>
        <button
          onClick={onCommit} disabled={busy}
          data-testid="commit-btn"
          className="inline-flex items-center gap-2 bg-[#1A3622] text-[#F9F8F6] px-5 py-2.5 text-sm border border-[#0F2818] hover:bg-[#0F2818] disabled:opacity-50"
        >
          {busy ? <Spinner size={14} className="animate-spin" /> : <Check size={14} weight="bold" />}
          {busy ? "Saving…" : `Import ${projected.length} item(s)`}
        </button>
      </div>
    </div>
  );
}
